import { 
  LayoutDashboard, 
  Users, 
  Store, 
  FileText, 
  Settings,
  CreditCard,
  BarChart,
  Briefcase,
  UserCircle,
  Award,
  Brain,
  MessageCircle,
  Shield,
  Target,
  TrendingUp,
  ShoppingBag,
  Smartphone,
  Tag,
  Building2,
  Package,
  Tags,
  Truck,
  ShoppingCart,
  Receipt,
  Gift,
  Globe,
  Sparkles,
  BarChart3,
  Circle,
  Ticket,
  ListChecks,
  Table,
  Dices,
  Box,
  ArrowLeftRight,
  Layers
} from "lucide-react";
import { config } from "@/config";

const BLOCKCHAIN_EXPLORER_ITEM = {
  name: "Blockchain Explorer",
  href: config.BLOCKCHAIN_EXPLORER_URL,
  icon: Globe,
  external: true,
} as const;

export interface SidebarItem {
  name: string;
  href: string;
  icon: any;
  /** Open href in a new browser tab (external URL) */
  external?: boolean;
}

export interface SidebarGroup {
  label?: string;
  items: SidebarItem[];
}

export interface SidebarConfig {
  appName: string;
  appShortName: string;
  appColor: string;
  groups: SidebarGroup[];
}

export const sidebarConfigs: Record<string, SidebarConfig> = {
  accesspoint: {
    appName: "Access Point",
    appShortName: "AP",
    appColor: "#3B82F6",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/accesspoint", icon: LayoutDashboard },
          { name: "Programs", href: "/accesspoint/programs", icon: Users },
          { name: "Merchants", href: "/accesspoint/merchants", icon: Store },
          {
            name: "Merchant App",
            href: "https://retail.staging.loyyal.io/",
            icon: Smartphone,
            external: true,
          },
          // { name: "Accounting Entries", href: "/accesspoint/accounting-entry", icon: FileText },
          { name: "Invoices", href: "/accesspoint/invoices", icon: Receipt },
          // { name: "Analytics", href: "/accesspoint/analytics", icon: TrendingUp },
          { name: "Reports", href: "/accesspoint/reports-insights", icon: BarChart },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      },
      {
        label: "Extras",
        items: [
          { name: "Jobs", href: "/accesspoint/jobs", icon: Briefcase },
          { name: "Users", href: "/users", icon: UserCircle },
          { name: "Clients", href: "/accesspoint/clients", icon: Building2 },
          { name: "Settings", href: "/settings", icon: Settings },
        ]
      }
    ]
  },
  gamepoint: {
    appName: "Game Point",
    appShortName: "GP",
    appColor: "#EA580C",
    groups: [
      {
        items: [
          { name: "Analytics", href: "/gamepoint/analytics", icon: BarChart3 },
          { name: "Spin a Wheel", href: "/gamepoint/spin-a-wheel", icon: Circle },
          { name: "Scratch Card", href: "/gamepoint/scratch-card", icon: Ticket },
          { name: "Slot Machine", href: "/gamepoint/slot-machine", icon: Dices },
          { name: "Mystery Box", href: "/gamepoint/mystery-box", icon: Box },
          { name: "Quiz", href: "/gamepoint/quiz", icon: ListChecks },
          { name: "Report", href: "/gamepoint/report", icon: Table },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ],
      },
    ],
  },
  perkpoint: {
    appName: "Perk Points",
    appShortName: "PP",
    appColor: "#22C55E",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/perkpoint", icon: LayoutDashboard },
          { name: "Segmentation", href: "/perkpoint/segmentation", icon: Users },
          { name: "Rule Engine", href: "/perkpoint/rule-engine", icon: Settings },
          { name: "Outlets", href: "/perkpoint/outlets", icon: Store },
          { name: "Campaign", href: "/perkpoint/campaign", icon: Target },
          { name: "Coupons", href: "/perkpoint/coupons", icon: Tag },
          { name: "Tier", href: "/perkpoint/tier", icon: Layers },
          { name: "Transaction", href: "/perkpoint/transaction", icon: ArrowLeftRight },
          { name: "Gamification", href: "/perkpoint/gamification", icon: Gift },
          { name: "Report", href: "/perkpoint/report", icon: FileText },
          { name: "Communication", href: "/perkpoint/communication", icon: MessageCircle },
          { name: "Member 360 degree", href: "/perkpoint/member-360", icon: UserCircle },
          {
            name: "Member Portal",
            href: "/member-details",
            icon: Globe,
            external: true,
          },
          { name: "User/Roles Management", href: "/perkpoint/user-roles-management", icon: UserCircle },
          { name: "Analytics", href: "/perkpoint/analytics", icon: BarChart },
          { name: "AI Chat", href: "/perkpoint/ai-chat", icon: MessageCircle },
          { name: "Fraud Detection", href: "/perkpoint/fraud-detection", icon: Shield },
          { name: "AI Recommendations", href: "/perkpoint/ai-recommendations", icon: Target },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      }
    ]
  },
  xpandpoint: {
    appName: "Xpand Point",
    appShortName: "XP",
    appColor: "#1EAEDB",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/xpandpoint", icon: LayoutDashboard },
          { name: "Program", href: "/xpandpoint/transfer-programs", icon: Building2 },
          { name: "Transfer Partners", href: "/xpandpoint/partners", icon: Award },
          {
            name: "Point Transfer App",
            href: "https://points-transfer.staging.loyyal.io/",
            icon: Smartphone,
            external: true,
          },
          { name: "Transactions", href: "/xpandpoint/transactions", icon: CreditCard },
          // { name: "Accounting Entries", href: "/xpandpoint/accounting-entry", icon: FileText },
          { name: "Invoices", href: "/xpandpoint/invoices", icon: Receipt },
          // { name: "Analytics", href: "/xpandpoint/analytics", icon: BarChart },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      },
      {
        label: "Extras",
        items: [
          { name: "Jobs", href: "/xpandpoint/jobs", icon: Briefcase },
          { name: "Users", href: "/xpandpoint/users", icon: UserCircle },
          { name: "Clients", href: "/xpandpoint/clients", icon: Building2 },
          { name: "Settings", href: "/xpandpoint/settings", icon: Settings },
        ]
      }
    ]
  },
  rewardpoint: {
    appName: "Reward Points",
    appShortName: "RP",
    appColor: "#8B5CF6",
    groups: [
      {
        items: [
          { name: "Programs", href: "/rewardpoint/programs", icon: Award },
          { name: "Brands", href: "/rewardpoint/brands", icon: Building2 },
          { name: "Products", href: "/rewardpoint/products", icon: Package },
          { name: "Platform Categories", href: "/rewardpoint/categories", icon: Tags },
          { name: "Redemption Merchants", href: "/rewardpoint/redemption-merchants", icon: Store },
          { name: "Shipping Profiles", href: "/rewardpoint/shipping-profiles", icon: Truck },
          { name: "Orders", href: "/rewardpoint/orders", icon: ShoppingCart },
          // { name: "Account Entries", href: "/rewardpoint/accounts", icon: Receipt },
          { name: "Invoices", href: "/rewardpoint/invoices", icon: FileText },
          {
            name: "Storefront",
            href: "https://rewardpoint.burnshop.staging.loyyal.io/",
            icon: ShoppingBag,
            external: true,
          },
          // { name: "Analytics", href: "/rewardpoint/analytics-new", icon: BarChart },
          // { name: "Users", href: "/users", icon: UserCircle },
          // { name: "Settings", href: "/settings", icon: Settings },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      },
      {
        label: "Extras",
        items: [
          { name: "Jobs", href: "/rewardpoint/jobs", icon: Briefcase },
          { name: "Users", href: "/rewardpoint/users", icon: UserCircle },
          { name: "Clients", href: "/rewardpoint/clients", icon: Building2 },
          { name: "Settings", href: "/rewardpoint/settings", icon: Settings },
        ]
      }
    ]
  },
  giftcard: {
    appName: "Gift OS",
    appShortName: "GC",
    appColor: "#F59E0B",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/giftcard", icon: LayoutDashboard },
          { name: "All Cards", href: "/giftcard/all-giftcards", icon: Gift },
          { name: "Issued Cards", href: "/giftcard/allissuedgiftcards", icon: CreditCard },
          { name: "Redeem Card", href: "/giftcard/redeem", icon: Gift },
          { name: "Campaign", href: "/giftcard/campaign", icon: Target },
          { name: "Brands", href: "/giftcard/brands", icon: Store },
          { name: "Mall", href: "/giftcard/mall", icon: Building2 },
          { name: "Logs", href: "/giftcard/logs", icon: FileText },
          { name: "Settings", href: "/giftcard/settings", icon: Settings },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      }
    ]
  },
  offers: {
    appName: "Offer Point",
    appShortName: "OP",
    appColor: "#E11D48",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/offers", icon: LayoutDashboard },
          { name: "Programs", href: "/offers/programs", icon: Users },
          { name: "Merchants", href: "/offers/merchants", icon: Store },
          { name: "Recommendation", href: "/offers/recommendation", icon: Sparkles },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      }
    ]
  }
};
